import { getAppVersion } from '@/lib/utils/app';
import { getUtmParams } from '@/lib/utils/utm';
import sessionService from '@/lib/services/session';
import playerService from '@/lib/services/player';
import { languageService } from '@/lib/services/language';
import { getEnvVariable, getNonEmptyEnvVariable } from '@/lib/utils/env';
import { getLoginSource } from '@/lib/utils/login';
import { webStorageUtil } from '@/lib/storage/webStorageUtil';
import { ELocalStorageKeys } from '@/lib/storage/enums';

/** Mixpanel store_version — identifies the Next.js store app (store-frontend parity). */
export const ANALYTICS_STORE_VERSION = 'v2';

const readVersionEnv = (
    nextPublicKey: 'NEXT_PUBLIC_STORE_VERSION' | 'NEXT_PUBLIC_SHARED_UI_VERSION' | 'NEXT_PUBLIC_CHECKOUT_SDK_VERSION',
    reactAppKey?: 'REACT_APP_SHARED_UI_VERSION' | 'REACT_APP_CHECKOUT_SDK_VERSION',
): string | null => {
    return getNonEmptyEnvVariable(nextPublicKey)
        ?? (reactAppKey ? getNonEmptyEnvVariable(reactAppKey) : undefined)
        ?? null;
};

const resolveAnalyticsEnvironment = (value: string | undefined): string | undefined => {
    if (!value || value === 'ephemeral') {
        return undefined;
    }
    return value;
};

export const getAnalyticsEnvironment = (): string => {
    return (
        resolveAnalyticsEnvironment(getNonEmptyEnvVariable('NEXT_PUBLIC_ENVIRONMENT'))
        ?? resolveAnalyticsEnvironment(getNonEmptyEnvVariable('REACT_APP_ENV'))
        ?? (getEnvVariable('NODE_ENV') === 'production' ? 'production' : 'local')
    );
};

export const getAnalyticsLanguage = (): string => {
    return languageService.getCurrentLanguageDisplayName();
};

export const getAnalyticsVersionProperties = () => ({
    store_version: ANALYTICS_STORE_VERSION,
    shared_ui_version: readVersionEnv('NEXT_PUBLIC_SHARED_UI_VERSION', 'REACT_APP_SHARED_UI_VERSION'),
    checkout_sdk_version: readVersionEnv('NEXT_PUBLIC_CHECKOUT_SDK_VERSION', 'REACT_APP_CHECKOUT_SDK_VERSION'),
});

export const getAnalyticsSessionId = (optionsSessionId?: unknown): string | null => {
    if (typeof optionsSessionId === 'string' && optionsSessionId.length > 0) {
        return optionsSessionId;
    }

    return (
        webStorageUtil.getSessionStorageWithFallback()?.id
        ?? sessionService.getSessionId()
        ?? null
    );
};

export const getAnalyticsSource = (): string => {
    return getLoginSource();
};

export const getPostLoginPlayerProperties = () => {
    const playerData = playerService.getPlayerData();

    return {
        ...(playerData?.playerId != null && { player_id: playerData.playerId }),
        ...(playerData?.playerCountry != null && { player_country: playerData.playerCountry }),
    };
};

export const collectAnalyticsEventProperties = () => {
    const playerData = playerService.getPlayerData();

    return {
        ...getUtmParams(),
        ...playerData ?? {},
        ...getPostLoginPlayerProperties(),
        ...getAnalyticsVersionProperties(),
        environment: getAnalyticsEnvironment(),
        source: getAnalyticsSource(),
        appVersion: getAppVersion(),
        sessionId: getAnalyticsSessionId(),
        session_id: getAnalyticsSessionId(),
        timestamp: new Date().getTime(),
        language: getAnalyticsLanguage(),
        city: null,
        country: null,
        publisher_id: webStorageUtil.get(ELocalStorageKeys.PUBLISHER_META)?.storeTheme?.publisherId,
    };
};
